import React, {useState, useEffect} from 'react';
import {Formik, Form} from 'formik';
import * as yup from 'yup';

import StepNavigation from '../StepNavigation';
import {
  InputWithSuggestions, FormikDropdownInput, TextInput,
} from '../../../components/Forms';
import {roleToRoleCategoryOptions} from '../../../util';
import {
  CountriesList, Countries, getCountryCode, getCountryName,
} from '../../../util/countries';
import {
  LEVEL_OPTIONS, WORK_TYPE_OPTIONS, RESULTS, computeJobTitle,
} from '../../../../../lib/utils';
import {Searcher} from '../../../../../lib/fuzzySearch';

const schema = yup.object({
  company: yup.string().required('Required'),
  role: yup.string().required('Required'),
  level: yup.string(),
  jobTitle: yup.string(),
  workType: yup.string(),
  location: yup.string().required('Required'),
  result: yup.string().required('Required'),
});

const OverviewStep = ({
  draft, tags, onNext, onEdit,
}) => {
  const [fuzzySearcher, setFuzzySearcher] = useState(null);
  const [filteredResults, setFilteredResults] = useState([]);
  const [countrySearcher, setCountrySearcher] = useState(null);
  const [filteredCountries, setFilteredCountries] = useState([]);
  const [jobTitleManuallyEdited, setJobTitleManuallyEdited] = useState(!!draft.jobTitle);

  // Hack to keep initialValues stable across renders,
  // preventing enableReinitialize from resetting form values
  const initialValues = React.useMemo(() => ({
    company: draft.company || '',
    role: draft.role || '',
    level: draft.level || '',
    jobTitle: draft.jobTitle
      || computeJobTitle(draft.role, draft.level, tags?.role),
    location: getCountryName(draft.location) || '',
    workType: draft.workType || '',
    result: draft.result || '',
  }), [draft.company, draft.role]);

  const handleCompanyBlur = (e) => {
    onEdit?.({company: e.target.value});
  };

  const handleLocationBlur = (e) => {
    onEdit?.({location: getCountryCode(e.target.value)});
  };

  useEffect(() => {
    if (tags?.company) {
      const searcher = new Searcher(tags.company, ['name']);
      setFuzzySearcher(searcher);
      setFilteredResults(searcher.search());
    }
  }, [tags]);

  useEffect(() => {
    const countriesWithId = CountriesList.map((code) => ({name: Countries[code], id: code}));
    const searcher = new Searcher(countriesWithId, ['name']);
    setCountrySearcher(searcher);
    setFilteredCountries(searcher.search());
  }, []);

  const updateCountrySuggestions = (query) => {
    if (countrySearcher) setFilteredCountries(countrySearcher.search(query));
  };

  const roleCategoryOptions = roleToRoleCategoryOptions(tags?.role, tags?.['role-category']);

  const updateSuggestions = (query) => {
    if (fuzzySearcher) setFilteredResults(fuzzySearcher.search(query));
  };

  const handleSubmit = (values) => {
    const {jobTitle, ...rest} = values;
    return onNext({
      ...rest,
      location: getCountryCode(values.location),
      ...(jobTitleManuallyEdited && {jobTitle}),
    });
  };

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={schema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({
        isValid, dirty, setFieldValue, values, isSubmitting,
      }) => {
        const handleRoleChange = (value) => {
          onEdit?.({role: value});
          if (!jobTitleManuallyEdited) {
            const newJobTitle = computeJobTitle(value, values.level, tags?.role);
            setFieldValue('jobTitle', newJobTitle);
          }
        };

        const handleLevelChange = (value) => {
          onEdit?.({level: value});
          if (!jobTitleManuallyEdited) {
            const newJobTitle = computeJobTitle(values.role, value, tags?.role);
            setFieldValue('jobTitle', newJobTitle);
          }
        };

        const handleJobTitleChange = () => {
          setJobTitleManuallyEdited(true);
        };

        const handleJobTitleBlur = (e) => {
          if (jobTitleManuallyEdited && onEdit) {
            onEdit({jobTitle: e.target.value});
          }
        };

        return (
          <Form className="flex flex-col gap-y-1">
            <InputWithSuggestions
              label="Where did you interview?"
              name="company"
              type="text"
              updateSuggestions={updateSuggestions}
              suggestions={filteredResults}
              required
              onBlur={handleCompanyBlur}
            />

            <FormikDropdownInput
              label="What type of role did you interview for?"
              name="role"
              options={roleCategoryOptions}
              showFilter
              required
              onSelect={handleRoleChange}
            />

            <FormikDropdownInput
              label="What level was the role?"
              name="level"
              placeholder="Select"
              options={LEVEL_OPTIONS.filter((o) => o.value)}
              onSelect={handleLevelChange}
            />

            <TextInput
              label="What was the job title?"
              name="jobTitle"
              autoComplete="off"
              onChange={handleJobTitleChange}
              onBlur={handleJobTitleBlur}
            />

            <FormikDropdownInput
              label="Was the role on-site or remote?"
              name="workType"
              placeholder="Select"
              options={WORK_TYPE_OPTIONS}
              onSelect={(v) => onEdit?.({workType: v})}
            />

            <InputWithSuggestions
              label="Where was the role based?"
              name="location"
              placeholder="Select"
              updateSuggestions={updateCountrySuggestions}
              suggestions={filteredCountries}
              strictSelection
              onBlur={handleLocationBlur}
              required
            />

            <FormikDropdownInput
              name="result"
              label="Did you receive an offer?"
              placeholder="Select"
              options={RESULTS}
              onSelect={(v) => onEdit?.({result: v})}
              required
            />

            <StepNavigation isSubmitting={isSubmitting} />
          </Form>
        );
      }}
    </Formik>
  );
};

export default OverviewStep;
